import { AnimatePresence, motion } from 'framer-motion'
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  Filter,
  Layers2,
  MessageSquareText,
  MoreHorizontal,
  Network,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CreateNoteDialog } from '@/components/graph/dialogs/CreateNoteDialog'
import { GlobalExpandConfirm } from '@/components/investigations/GlobalExpandConfirm'
import { SidebarOpenButton } from '@/components/investigations/SidebarOpenButton'
import { filterFaceEvidence, shownFaceIds } from '@/components/knowledge-graph-view/face-visibility'
import { CreateRelationshipDialog } from '@/components/neo4j/CreateRelationshipDialog'
import type { CytoscapeGraphRef } from '@/components/neo4j/cytoscape/CytoscapeGraph'
import CytoscapeGraph from '@/components/neo4j/cytoscape/CytoscapeGraph'
import { ExpandNeighborsPopover } from '@/components/neo4j/cytoscape/ExpandNeighborsPopover'
import { LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import {
  applyPathHighlight,
  // Aliased: the cockpit-store's `clearPathHighlight` selector (store state)
  // shadows this canvas-level helper inside the component body.
  clearPathHighlight as clearPathHighlightOnCanvas,
} from '@/components/neo4j/cytoscape/path-highlight'
import {
  type GraphEdge,
  type GraphNode,
  isClusterNodeId,
  type SelectedEdge,
  type SelectedNode,
} from '@/components/neo4j/cytoscape/types'
import { ConnectionTargetBanner } from '@/components/neo4j/shared/ConnectionTargetBanner'
import type { NeighborEdge, NeighborNode } from '@/components/neo4j/shared/entity-details/types'
import { MultiSelectActionBar } from '@/components/neo4j/shared/MultiSelectActionBar'
import { PathResultBanner } from '@/components/neo4j/shared/PathResultBanner'
import { useGraphConnection } from '@/components/neo4j/shared/useGraphConnection'
import {
  buildDensity,
  fitBounds,
  overlapsRange,
  readTemporalExtent,
  TimeRangeFilter,
} from '@/components/time-range-filter'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/contexts/AuthContext'
import { applyFactVisibility, unionIds } from '@/features/analyst-facts/fact-marks'
import { NoLongerValidToggle } from '@/features/analyst-facts/NoLongerValidToggle'
import { useShowNoLongerValid } from '@/features/analyst-facts/useFactVisibility'
import { entityUid } from '@/features/entity-history/history-rows'
import { useChangedEntities } from '@/features/entity-history/useEntitySummaries'
import { resolveInsight } from '@/features/insights/utils/resolveInsight'
import { useInsights } from '@/features/insights/utils/useInsights'
import { CreateEntityDialog } from '@/features/manual-graph/CreateEntityDialog'
import type { CanvasNode } from '@/features/manual-graph/graph-node'
import { useCanvasEntityCreation } from '@/features/manual-graph/hooks/useCanvasEntityCreation'
import { useCreateManualRelationship } from '@/features/manual-graph/hooks/useManualGraph'
import { RegisterPicker } from '@/features/registers/RegisterPicker'
import { snippetGraphNodes, snippetSelection } from '@/features/source-snippets/object-set'
import { useSnippetSelection } from '@/features/source-snippets/selection'
import {
  ADVANCED_FACE_DETECTION_EXTENSION_ID,
  OSINT_EXTENSION_ID,
  useExtensionEnabled,
} from '@/hooks/use-extensions'
import { isMobileViewport } from '@/hooks/use-mobile'
import { useGraphExclusion } from '@/hooks/useGraphExclusion'
import { useNeighborCounts } from '@/hooks/useNeighborCounts'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { graphApi } from '@/services/api/graph'
import { useCockpitStore } from '@/stores/cockpit-store'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import { useHiddenNodeIds } from '@/stores/object-set-contributions-store'
import { useSelectionStore } from '@/stores/selection-store'
import { useTimeFilterStore } from '@/stores/time-filter-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import { cn } from '@/utils/cn'
import { getEntityDisplayLabel } from '@/utils/entity-display'
import { ChangedNodeMarker } from './_internal/ChangedNodeMarker'
import { useNodeCornerAnchors } from './_internal/useNodeCornerAnchors'

type LayoutId =
  | 'fcose'
  | 'cola'
  | 'cose'
  | 'grid'
  | 'circle'
  | 'breadthfirst'
  | 'concentric'
  | 'clusters'
  | 'custom'

/** Outcome of the ring-menu Find Path tool, pending the user's keep/dismiss
 *  decision in the PathResultBanner. `addedNodes`/`addedEdges` are the path
 *  elements Neo4j returned that were not rendered — shown provisionally,
 *  committed to the object set only on Keep. The intersection type keeps
 *  them assignable to both the canvas data (GraphNode) and `mergeIntoView`
 *  (NeighborNode, whose `labels` is required). */
interface PendingPathResult {
  nodeIds: string[]
  edgeIds: string[]
  pathLength: number
  addedNodes: Array<GraphNode & NeighborNode>
  addedEdges: GraphEdge[]
}

interface InvestigationCanvasProps {
  /** Optional: rendered inside the cytoscape canvas area (above the bottom
   *  TimeRangeFilter). Lets the host page float its InvestigationSidebar over
   *  the graph without covering the full-width time-range minimap at the
   *  bottom — mirrors how the Geo and Time Axis views compose their sidebar. */
  sidebarSlot?: ReactNode
  /** Current visibility of the host-provided sidebar. When `false`, the
   *  toolbar appends a primary "open sidebar" button (same affordance as the
   *  Map toolbar) AND any node/edge click force-opens the sidebar so the
   *  user always sees the detail they just selected. */
  sidebarOpen?: boolean
  /** Host-provided opener. Required for the sidebar button + force-open
   *  behaviour to engage; without it those interactions are no-ops. */
  onOpenSidebar?: () => void
}

export function InvestigationCanvas({
  sidebarSlot,
  sidebarOpen = true,
  onOpenSidebar,
}: InvestigationCanvasProps = {}) {
  const { t } = useTranslation()
  const { caseId } = useCaseContext()
  const transient = useInvestigationsStore(s => s.transient)
  const activeId = useInvestigationsStore(s => s.activeId)
  const active = useInvestigationsStore(s =>
    activeId ? (s.saved.find(i => i.id === activeId) ?? null) : null
  )
  const storeApi = useInvestigationsStoreApi()
  const { insights } = useInsights(caseId)

  const graphRef = useRef<CytoscapeGraphRef>(null)
  // Register lookup lives behind the OSINT extension, like the rest of that
  // surface. `enabled` is false until the extensions list arrives, so the ring
  // entry appears once rather than flashing in.
  const { enabled: osintEnabled } = useExtensionEnabled(OSINT_EXTENSION_ID)
  // Saved face evidence stays hidden while Advanced Face Detection is off (ADR-0051).
  const { enabled: faceDetection } = useExtensionEnabled(ADVANCED_FACE_DETECTION_EXTENSION_ID)
  const [registerAnchorId, setRegisterAnchorId] = useState<string | null>(null)
  // Set when an import adds elements; consumed by the effect below once React
  // has actually pushed them into cytoscape. `updateData` adds elements without
  // running a layout, so newcomers would otherwise stack at the origin.
  const [pendingLayout, setPendingLayout] = useState(false)
  const [multiSelectedNodeIds, setMultiSelectedNodeIds] = useState<string[]>([])
  // Outer canvas wrapper — the `relative` parent the +N badges position
  // themselves against. Capturing it via ref decouples badge math from any
  // wrapping CytoscapeGraph chrome that may sit in between.
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null)
  const [layout, setLayout] = useState<LayoutId>('fcose')

  const isValidLayout = (v: unknown): v is LayoutId =>
    typeof v === 'string' &&
    [
      'fcose',
      'cola',
      'cose',
      'grid',
      'circle',
      'breadthfirst',
      'concentric',
      'clusters',
      'custom',
    ].includes(v)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [notesCollapsed, setNotesCollapsed] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editingNoteDraft, setEditingNoteDraft] = useState('')
  const [savingNoteEdit, setSavingNoteEdit] = useState(false)
  const [composingNote, setComposingNote] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  // Per-entity note creation from the ring menu. Opens the same
  // CreateNoteDialog the graph explorer uses; `addNoteSourceId` is null when
  // the entry point is a non-entity-scoped action (none today, but the dialog
  // supports it so we keep the prop type loose).
  const [addNoteOpen, setAddNoteOpen] = useState(false)
  const [addNoteSourceId, setAddNoteSourceId] = useState<string | null>(null)
  const { user } = useAuth()
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [showGlobalExpand, setShowGlobalExpand] = useState(false)
  // Surfaced when the user tries to create a connection whose source or
  // target no longer exists in the case's Neo4j graph (entity merged,
  // deleted, or otherwise removed since this view was loaded). Checked
  // against the server before issuing the create — the local view is not
  // authoritative.
  const [divergenceWarning, setDivergenceWarning] = useState<{
    missingId: string
    missingName: string
    side: 'source' | 'target'
  } | null>(null)
  // Visibility of the bottom time-range strip. Mirrors the map view's
  // `timelineVisible` flag (geolocation-store) but kept local because the
  // graph has no equivalent store slice. Toggled from the view-tools
  // fan-out button in the toolbar. Phones start with it collapsed: the strip
  // would take a third of the canvas height.
  const [timelineVisible, setTimelineVisible] = useState(() => !isMobileViewport())

  // Per-view filter state. Namespaced under "investigation-graph-" so it
  // doesn't collide with case-graph-explorer / KnowledgeGraphView (which key
  // their exclusion by raw caseId). All investigations in this case share the
  // same hidden-types set on the Graph view — the Map view will keep its own.
  const exclusionState = useGraphExclusion(`investigation-graph-${caseId}`)

  const view = transient?.graphView ?? active?.graphView ?? null
  const canvasKey = transient?.fromInsightId ?? activeId ?? 'empty'
  const visibleNotes = active?.notes ?? []

  const notesInvestigationRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const switched = notesInvestigationRef.current !== activeId
    notesInvestigationRef.current = activeId
    if (visibleNotes.length === 0) setNotesCollapsed(true)
    else if (!isMobileViewport()) setNotesCollapsed(false)
    // Phones start collapsed when an investigation opens (the open panel
    // covers the top of the canvas; the header still shows the count), but a
    // note added or removed afterwards must not snap the panel shut.
    else if (switched) setNotesCollapsed(true)
  }, [activeId, visibleNotes.length])

  // Cross-view selection — sidebar (and other views) write into the canonical
  // selection-store. Mirror that into cytoscape so a sidebar click visibly
  // highlights the corresponding node, matching how the map view behaves.
  const externalSelectedId = useSelectionStore(s => firstSelectedId(s.selectedEntityIds))
  // Track whether we've seen an actual selection change since this canvas
  // mounted. The first sync just highlights — flying-to before the fcose
  // layout has settled fights with it and ends up with a tightly-zoomed view
  // on stacked initial positions. Subsequent user-driven changes fly.
  const lastSyncedSelectedId = useRef<string | null | undefined>(undefined)
  // Explicit "take me to this entity" requests (chat tool results, sidebar
  // rows) bump a nonce in the selection store. Those must fly even on the
  // first sync after mount — that is precisely when someone clicks a chat
  // result and lands on a canvas they haven't touched yet — so the nonce
  // overrides the first-sync suppression above.
  const externalFocusNonce = useSelectionStore(s => s.focusNonce)
  const lastHandledFocusNonce = useRef(externalFocusNonce)
  // Skip-flyTo flag: set by the on-canvas tap handlers below before they push
  // into the selection store. The mirror effect then sees this id and bypasses
  // its camera animation — the user just clicked the node at that screen
  // position, panning to it is jarring (combined with the sidebar slide-in it
  // reads as a "jolt"). Sidebar / chat / cross-view selections still fly.
  const skipNextFlyForRef = useRef<string | null>(null)
  const skipNextFlyForEdgeRef = useRef<string | null>(null)

  // Reset the "first sync" flag when CytoscapeGraph remounts (different
  // investigation/insight = new cy instance, fresh fcose layout to wait on).
  useEffect(() => {
    lastSyncedSelectedId.current = undefined
  }, [canvasKey])

  // Tracks whether the underlying cy instance is live. Effects that need to
  // touch cy (e.g. path highlight on first navigation from chat) wait on this
  // instead of racing with `setTimeout(100)`. A 2s safety timeout flips it on
  // if the onReady callback never fires (defensive — shouldn't happen).
  const [cyReady, setCyReady] = useState(false)
  useEffect(() => {
    setCyReady(false)
    const t = setTimeout(() => setCyReady(true), 2000)
    return () => clearTimeout(t)
  }, [canvasKey])

  useEffect(() => {
    const cy = graphRef.current?.getCy?.()
    // Leave the nonce unhandled so this re-runs and still flies once cy is
    // live — navigating in from another route arrives here before the canvas.
    if (!cy) return
    const prev = lastSyncedSelectedId.current
    lastSyncedSelectedId.current = externalSelectedId
    const focusRequested = externalFocusNonce !== lastHandledFocusNonce.current
    if (!externalSelectedId) {
      lastHandledFocusNonce.current = externalFocusNonce
      if (cy.elements(':selected').length > 0) {
        graphRef.current?.clearSelection()
      }
      return
    }
    const node = cy.$id(externalSelectedId)
    if (node.length === 0) return // selected entity isn't in this graph view
    lastHandledFocusNonce.current = externalFocusNonce
    // Already highlighted — normally nothing to do, but an explicit focus
    // request still owes the user the camera move that makes it findable.
    if (node.selected() && !focusRequested) return
    graphRef.current?.selectNode(externalSelectedId)
    if (prev !== undefined || focusRequested) {
      // Skip the pan when this id was just selected by an on-canvas tap —
      // the node is already at the click point, animating to it produces
      // the visible "jolt" the user feels alongside the sidebar slide-in.
      if (skipNextFlyForRef.current === externalSelectedId) {
        skipNextFlyForRef.current = null
      } else {
        // User-driven selection change from outside the canvas (sidebar,
        // chat, …): animate the camera to centre on the chosen node.
        // Keeping the existing zoom preserves spatial context while still
        // pulling the entity into focus — mirrors the map's flyTo. We
        // hand-roll an RAF interpolation because cy.animate's pan/center
        // options are silently no-ops in this setup (likely a layout queue
        // interaction); cy.pan() works synchronously.
        flyToNode(cy, node)
      }
    }
    // Re-run on `view` / `cyReady` changes so a freshly-rendered graph picks
    // up the current selection (e.g. after switching investigations, or when
    // arriving from another route before cytoscape was live).
  }, [externalSelectedId, externalFocusNonce, cyReady, view])

  // Mirror canonical edge selection into cytoscape so clicking a relationship
  // row in the sidebar visibly highlights the edge in the graph and pulls it
  // into view — matches the entity-selection flow above. The graph itself
  // writes back via `onEdgeSelect` and that write is a no-op when the value
  // doesn't change, so this stays a one-way mirror from store → canvas.
  const externalSelectedEdgeId = useSelectionStore(s => s.selectedEdgeId)
  const lastSyncedSelectedEdgeId = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    lastSyncedSelectedEdgeId.current = undefined
  }, [canvasKey])

  useEffect(() => {
    const cy = graphRef.current?.getCy?.()
    if (!cy) return
    const prev = lastSyncedSelectedEdgeId.current
    lastSyncedSelectedEdgeId.current = externalSelectedEdgeId
    if (!externalSelectedEdgeId) {
      if (cy.edges(':selected').length > 0) {
        cy.edges().unselect()
      }
      return
    }
    const edge = cy.$id(externalSelectedEdgeId)
    if (edge.length === 0 || !edge.isEdge()) return
    if (edge.selected()) return
    graphRef.current?.selectEdge(externalSelectedEdgeId)
    if (prev !== undefined) {
      // Skip-fly when this edge was just clicked on the canvas — see the
      // node mirror above for the rationale.
      if (skipNextFlyForEdgeRef.current === externalSelectedEdgeId) {
        skipNextFlyForEdgeRef.current = null
      } else {
        // User-driven change from the sidebar — gently centre the camera
        // on the edge's midpoint (the rendered position between its
        // endpoints) while keeping the existing zoom. Mirrors the node
        // flyTo above.
        const source = edge.source()
        const target = edge.target()
        if (source.length > 0 && target.length > 0) {
          const midpoint = {
            x: (source.position('x') + target.position('x')) / 2,
            y: (source.position('y') + target.position('y')) / 2,
          }
          flyToPoint(cy, midpoint)
        }
      }
    }
  }, [externalSelectedEdgeId, view])

  // Path highlighting from cockpit store (set by agent tools like FindShortestPath)
  const pathHighlight = useCockpitStore(s => s.pathHighlight)
  const clearPathHighlight = useCockpitStore(s => s.clearPathHighlight)

  useEffect(() => {
    if (!pathHighlight) return
    if (!cyReady) return
    const cy = graphRef.current?.getCy?.()
    if (!cy) return

    // cy is live and graphData is synced — apply immediately, no timing race.
    // (Escape dismissal lives in CytoscapeGraph's keyboard handler.)
    applyPathHighlight(cy, pathHighlight.nodeIds, pathHighlight.edgeIds)

    // Clear the path highlight from store after applying (one-shot).
    // Defer to next tick so the consuming effect's render has committed.
    const clearTimer = setTimeout(clearPathHighlight, 0)

    return () => {
      clearTimeout(clearTimer)
    }
  }, [pathHighlight, cyReady, clearPathHighlight])

  // Raw node shape from the saved investigation — no neighbour metadata yet.
  // Decorated with unloaded-neighbour counts below in `nodesWithCounts`.
  const { nodes: baseNodes, edges } = useMemo(() => {
    if (!view) return { nodes: [], edges: [] }
    const nodes = [...view.nodes, ...snippetGraphNodes(view.snippets ?? [])].map(n => ({
      id: n.id,
      name: n.name ?? n.id,
      labels: n.labels,
      typeLabel: n.typeLabel ?? n.labels[0],
      properties: n.properties,
    }))
    return filterFaceEvidence(
      nodes,
      view.edges,
      shownFaceIds(view.view.expandedFaceIds, faceDetection)
    )
  }, [view, faceDetection])

  // Unloaded-neighbour counts — fed into the "+N" badge style and the
  // per-node "Expand…" popover. The hook handles caching and only fires
  // when there's a non-empty visible set.
  const baseNodeIds = useMemo(
    () => baseNodes.filter(n => !snippetSelection(n.id)).map(n => n.id),
    [baseNodes]
  )
  const neighborCountsQuery = useNeighborCounts(caseId, baseNodeIds)
  const neighborCounts = neighborCountsQuery.data?.counts ?? {}
  const decoratedBaseNodes = useMemo<GraphNode[]>(() => {
    if (Object.keys(neighborCounts).length === 0) return baseNodes
    return baseNodes.map(n => {
      const byLabel = neighborCounts[n.id]
      if (!byLabel) return n
      const total = Object.values(byLabel).reduce((s, c) => s + c, 0)
      if (total <= 0) return n
      return { ...n, unloadedNeighborCount: total, unloadedNeighborsByLabel: byLabel }
    })
  }, [baseNodes, neighborCounts])

  // Per-view visibility filter: hide nodes whose typeLabel (or individual id)
  // is excluded, and drop dangling edges whose endpoints are no longer
  // visible. The underlying Object Set is unchanged — this only narrows what
  // gets rendered. baseNodes/edges remain the source of truth so detail
  // popups, expand-neighbours, etc. still see the full dataset.
  // Chip toggles (object-set contributions) can hide a slice of the view
  // without removing it from the underlying investigation. Read the union of
  // node ids contributed by disabled chips for the current viewpoint and
  // subtract them at the same layer as the explicit exclusion filter.
  const investigationKey = activeId ?? (transient ? 'transient' : null)
  const hiddenContributionNodeIds = useHiddenNodeIds(investigationKey ?? undefined)

  const shownNodes = useMemo<GraphNode[]>(() => {
    const { hiddenEntityTypes, hiddenEntityIds } = exclusionState.exclusion
    const hasExclusions = hiddenEntityTypes.length > 0 || hiddenEntityIds.length > 0
    const hasHiddenContributions = hiddenContributionNodeIds.size > 0
    if (!hasExclusions && !hasHiddenContributions) return decoratedBaseNodes
    const hiddenTypes = new Set(hiddenEntityTypes)
    const hiddenIds = new Set(hiddenEntityIds)
    return decoratedBaseNodes.filter(
      n =>
        !hiddenTypes.has(n.typeLabel || 'Unknown') &&
        !hiddenIds.has(n.id) &&
        !hiddenContributionNodeIds.has(n.id)
    )
  }, [decoratedBaseNodes, exclusionState.exclusion, hiddenContributionNodeIds])

  // What an analyst marked no longer valid or wrong stays off the canvas unless the switch shows
  // it, greyed (PRD-0075 R-C4); an edge of a hidden node goes with it.
  const [showNoLongerValid] = useShowNoLongerValid()
  const factView = useMemo(
    () => applyFactVisibility(shownNodes, edges, showNoLongerValid),
    [shownNodes, edges, showNoLongerValid]
  )
  const visibleNodes = factView.nodes

  // Always drop dangling edges — saved investigations can hold edges whose
  // endpoint nodes were never persisted (or got pruned), and cytoscape will
  // throw on `cy.add(...)` for an edge whose target id isn't a known node.
  // The throw aborts initialisation entirely, so the canvas would be blank
  // even though `nodes` looks fine. Filter is then a cheap incremental step.
  const visibleEdges = useMemo<GraphEdge[]>(() => {
    const visibleIds = new Set(visibleNodes.map(n => n.id))
    return factView.edges.filter(e => visibleIds.has(e.source) && visibleIds.has(e.target))
  }, [factView.edges, visibleNodes])

  // ── Time-range filter ────────────────────────────────────────────────
  // Read backend-stamped `_temporal_min` / `_temporal_max` from each node.
  // Nodes with no temporal stamp stay fully visible — the filter only
  // affects time-anchored entities. Out-of-range nodes get the
  // `timeline-dimmed` class via CytoscapeGraph's existing `dimmedNodeIds`
  // prop (originally used by the playback feature).
  const timeExtents = useMemo(() => {
    const out = new Map<string, { start: number; end: number }>()
    for (const n of visibleNodes) {
      const ext = readTemporalExtent(n.properties as Record<string, unknown> | undefined)
      if (ext) out.set(n.id, ext)
    }
    return out
  }, [visibleNodes])

  const timeBounds = useMemo(() => {
    const starts: number[] = []
    for (const ext of timeExtents.values()) {
      starts.push(ext.start, ext.end)
    }
    return fitBounds(starts)
  }, [timeExtents])

  const timeDensityStamps = useMemo(() => {
    const out: number[] = []
    for (const ext of timeExtents.values()) out.push(ext.start)
    return out
  }, [timeExtents])

  const timeDensity = useMemo(
    () => (timeBounds ? buildDensity(timeBounds, timeDensityStamps) : undefined),
    [timeBounds, timeDensityStamps]
  )

  const graphTimeFilter = useTimeFilterStore(s => s.graph)
  const setGraphTimeFilter = useTimeFilterStore(s => s.setFilter)

  // Clear stale filter if the new dataset's bounds fully exclude it. Avoids a
  // confusing "filter active but nothing matches" state after switching
  // investigations.
  useEffect(() => {
    if (!graphTimeFilter || !timeBounds) return
    const outside = graphTimeFilter.end < timeBounds.start || graphTimeFilter.start > timeBounds.end
    if (outside) setGraphTimeFilter('graph', null)
  }, [graphTimeFilter, timeBounds, setGraphTimeFilter])

  const dimmedTimeNodeIds = useMemo(() => {
    if (!graphTimeFilter) return null
    const out = new Set<string>()
    for (const n of visibleNodes) {
      const ext = timeExtents.get(n.id)
      if (!overlapsRange(ext ?? null, graphTimeFilter)) out.add(n.id)
    }
    return out
  }, [graphTimeFilter, visibleNodes, timeExtents])

  const dimmedTimeEdgeIds = useMemo(() => {
    if (!dimmedTimeNodeIds || dimmedTimeNodeIds.size === 0) return null
    const out = new Set<string>()
    for (const e of visibleEdges) {
      if (dimmedTimeNodeIds.has(e.source) || dimmedTimeNodeIds.has(e.target)) out.add(e.id)
    }
    return out
  }, [dimmedTimeNodeIds, visibleEdges])

  const timeFilteredVisibleCount = visibleNodes.length - (dimmedTimeNodeIds?.size ?? 0)
  // Greyed: out of the time range, or shown by the "no longer valid" switch.
  const dimmedNodeIds = useMemo(
    () => unionIds(dimmedTimeNodeIds, factView.faded),
    [dimmedTimeNodeIds, factView.faded]
  )
  const dimmedEdgeIds = useMemo(
    () => unionIds(dimmedTimeEdgeIds, factView.faded),
    [dimmedTimeEdgeIds, factView.faded]
  )

  // Type buckets for the filter panel — counts across the unfiltered set so
  // hidden types still appear (you need to see them to toggle them back).
  // Sorted by LABEL_COLORS' canonical order, then alphabetically for unknown
  // types. Mirrors how ExploreFilterPanel computes its list.
  const nodeTypeStats = useMemo(() => {
    const counts = new Map<string, number>()
    for (const n of baseNodes) {
      const type = n.typeLabel || n.labels?.[0] || 'Unknown'
      counts.set(type, (counts.get(type) ?? 0) + 1)
    }
    const order = Object.keys(LABEL_COLORS)
    const hidden = new Set(exclusionState.exclusion.hiddenEntityTypes)
    return Array.from(counts.entries())
      .sort(([a], [b]) => {
        const ia = order.indexOf(a)
        const ib = order.indexOf(b)
        const oa = ia === -1 ? order.length : ia
        const ob = ib === -1 ? order.length : ib
        if (oa !== ob) return oa - ob
        return a.localeCompare(b)
      })
      .map(([type, count]) => ({
        type,
        count,
        isHidden: hidden.has(type),
        color: LABEL_COLORS[type] ?? { fill: '#64748b', stroke: '#475569' },
      }))
  }, [baseNodes, exclusionState.exclusion.hiddenEntityTypes])

  const hiddenTypesCount = exclusionState.exclusion.hiddenEntityTypes.length

  // For the "Clusters (Louvain)" layout cytoscape needs compound parent nodes
  // grouping children that share `component_id`. Mirrors the same derivation
  // KnowledgeGraphView does.
  const nodes = useMemo<GraphNode[]>(() => {
    if (layout !== 'clusters') return visibleNodes
    const byComp = new Map<number, GraphNode[]>()
    for (const n of visibleNodes) {
      const compRaw = (n.properties as Record<string, unknown> | undefined)?.component_id
      if (compRaw == null) continue
      const comp = Number(compRaw)
      if (!byComp.has(comp)) byComp.set(comp, [])
      byComp.get(comp)!.push(n)
    }
    if (byComp.size < 2) return visibleNodes
    const sortedComps = [...byComp.keys()].sort((a, b) => a - b)
    const compounds: GraphNode[] = sortedComps.map((comp, idx) => ({
      id: `__cluster_${comp}`,
      name: `Cluster ${String.fromCharCode(65 + idx)}`,
      labels: ['CaseCompound'],
      typeLabel: 'CaseCompound',
      properties: { component_id: comp, size: byComp.get(comp)!.length },
      isCompound: true,
    }))
    const reparented = visibleNodes.map(n => {
      const compRaw = (n.properties as Record<string, unknown> | undefined)?.component_id
      if (compRaw == null) return n
      return { ...n, parent: `__cluster_${Number(compRaw)}` }
    })
    return [...compounds, ...reparented]
  }, [layout, visibleNodes])

  // ---------------------------------------------------------------------
  // Refresh the current source.
  // ---------------------------------------------------------------------
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const fromInsightId = storeApi.getState().transient?.fromInsightId
      if (fromInsightId) {
        const insight = insights.find(i => i.id === fromInsightId)
        if (!insight) {
          toast.info('Insight no longer available — try Refresh on the strip first')
          return
        }
        const fresh = await resolveInsight(caseId, insight)
        storeApi.getState().setTransient(fresh, insight.title, insight.id)
        return
      }
      if (storeApi.getState().activeId) {
        await storeApi.getState().fetchAll()
      }
    } catch (err) {
      toast.error(`Refresh failed: ${(err as Error).message}`)
    } finally {
      setIsRefreshing(false)
    }
  }, [caseId, insights, storeApi])

  // ---------------------------------------------------------------------
  // Ring menu — Create Connection between two nodes.
  //
  // Flow: ring menu "Create Connection" → connection mode active → user
  // taps the target node (intercepted in `onNodeSelect` below) →
  // <CreateRelationshipDialog> opens for the user to pick a relationship
  // type (including POSSIBLE_MATCH) → divergence pre-check (both endpoints
  // must still be in the curated view) → API call writes the edge →
  // mergeIntoView pushes it into the active investigation so it appears
  // immediately, and the saved investigation is marked dirty.
  // ---------------------------------------------------------------------
  const createManualRelationship = useCreateManualRelationship(caseId)
  const graphConnection = useGraphConnection({
    onCreateConnection: async (sourceId, targetId, _sName, _tName, relationshipType) => {
      const newRel = await createManualRelationship.mutateAsync({
        source_id: sourceId,
        target_id: targetId,
        type: relationshipType,
      })
      // Push the freshly-created edge into the active investigation view.
      // No new nodes — both endpoints already exist (we validated that in
      // the divergence pre-check below).
      mergeIntoView(
        [],
        [
          {
            id: newRel.id,
            source: newRel.source,
            target: newRel.target,
            type: newRel.type,
            properties: newRel.properties ?? {},
          },
        ]
      )
    },
  })

  // ---------------------------------------------------------------------
  // Ring menu — Find Path between two nodes.
  //
  // Same pick gesture as Create Connection: the ring menu arms the mode with
  // the right-clicked node as source (`pathSourceId` non-null ⇔ mode armed),
  // the next tap on another node (intercepted in `onNodeSelect` below) is the
  // target. The path comes from Neo4j — the same shortestPath engine the AI
  // assistant's find_shortest_path tool uses — so it may run through entities
  // outside the current view. Those render provisionally (appended to the
  // canvas data below, NOT written to the object set) and the
  // PathResultBanner asks whether to keep them: Keep commits them via
  // `mergeIntoView`, Dismiss/Esc drops them together with the highlight.
  // ---------------------------------------------------------------------
  const [pathSourceId, setPathSourceId] = useState<string | null>(null)
  const cancelPathPick = useCallback(() => setPathSourceId(null), [])
  const [pathResult, setPathResult] = useState<PendingPathResult | null>(null)
  // Staleness plumbing: the request runs across an await, during which the
  // user can switch investigations, re-arm a pick mode, or mutate the view.
  // The token invalidates in-flight responses; the refs let the resolver diff
  // against the view as it is at resolve time, not at click time.
  const pathRequestRef = useRef(0)
  const pathResultRef = useRef(pathResult)
  pathResultRef.current = pathResult
  const visibleNodesRef = useRef(visibleNodes)
  visibleNodesRef.current = visibleNodes
  const visibleEdgesRef = useRef(visibleEdges)
  visibleEdgesRef.current = visibleEdges

  // A new investigation/insight = a new canvas: drop any armed pick, any
  // pending result (its provisional elements belong to the old view —
  // leaking them into the remounted cytoscape crashes init on dangling
  // edges, and Keep would write into the wrong investigation), and any
  // in-flight request.
  useEffect(() => {
    pathRequestRef.current++
    setPathSourceId(null)
    setPathResult(null)
  }, [canvasKey])

  const findAndHighlightPath = useCallback(
    async (sourceId: string, targetId: string) => {
      const token = ++pathRequestRef.current
      try {
        const res = await graphApi.findShortestPath(caseId, {
          source_node_id: sourceId,
          target_node_id: targetId,
          max_depth: 10,
        })
        // Stale: the canvas switched, another pick mode armed, or a newer
        // request superseded this one while it was in flight.
        if (token !== pathRequestRef.current) return
        if (!res.found) {
          const nameOf = (id: string) => {
            const node = baseNodes.find(n => n.id === id)
            return node ? getEntityDisplayLabel(node) : id
          }
          toast.info(
            t('investigations.graph.pathTool.noPath', {
              source: nameOf(sourceId),
              target: nameOf(targetId),
            })
          )
          return
        }
        // Split the path into what's already rendered and what Neo4j pulled
        // in from outside the view. Compared against the *rendered* sets so a
        // filter-hidden path node is re-shown provisionally (a gap in the
        // highlight would read as a broken path); Keep re-checks against the
        // full object set before committing. Read through the refs — the view
        // may have changed while the request was in flight.
        const renderedNodeIds = new Set(visibleNodesRef.current.map(n => n.id))
        const renderedEdgeIds = new Set(visibleEdgesRef.current.map(e => e.id))
        setPathResult({
          nodeIds: res.node_ids,
          edgeIds: res.relationship_ids,
          pathLength: res.path_length,
          addedNodes: res.nodes
            .filter(n => !renderedNodeIds.has(n.id))
            .map(n => ({
              id: n.id,
              name: n.name,
              labels: n.labels,
              typeLabel: n.labels[0],
              properties: n.properties ?? {},
            })),
          addedEdges: res.relationships
            .filter(e => !renderedEdgeIds.has(e.id))
            .map(e => ({
              id: e.id,
              type: e.type,
              source: e.source,
              target: e.target,
              properties: e.properties ?? {},
            })),
        })
      } catch (err) {
        if (token === pathRequestRef.current) {
          toast.error(`Find path failed: ${(err as Error).message}`)
        }
      }
    },
    [caseId, baseNodes, t]
  )

  // Source entity shown in the shared target-pick banner below. Guarded so
  // the O(n) lookup only runs while one of the pick modes is armed.
  const pickSourceId = graphConnection.connectionSourceId ?? pathSourceId
  const pickSource = pickSourceId ? baseNodes.find(n => n.id === pickSourceId) : undefined

  // The entity the register picker was opened on. Resolved from the rendered
  // set rather than held in state so it stays in step with a rename or a merge
  // that lands while the dialog is open.
  const registerAnchor = useMemo(() => {
    if (!registerAnchorId) return null
    const node = baseNodes.find(n => n.id === registerAnchorId)
    if (!node) return null
    return {
      id: node.id,
      name: node.name,
      typeLabel: node.typeLabel ?? node.labels?.[0],
      properties: node.properties,
    }
  }, [baseNodes, registerAnchorId])

  /** Route a list of new nodes/edges into the current viewpoint (transient
   * or active saved investigation) via the store's `addToObjectSet`, so all
   * add paths — graph expand, "Add neighbours" in the sidebar, insights,
   * agent — share the same dirty-flag flow. Edits to a saved investigation
   * stay client-side until the user explicitly saves; the
   * `InvestigationDirtyNotifier` surfaces an inline Save / Discard toast on
   * the first change. */
  const mergeIntoView = useCallback(
    (newNodes: NeighborNode[], newEdges: NeighborEdge[]) => {
      if (newNodes.length === 0 && newEdges.length === 0) return
      const state = storeApi.getState()
      const view: GraphView = {
        meta: {
          source: 'chips',
          createdAt: new Date().toISOString(),
          caseId: state.caseId,
        },
        nodes: newNodes.map(n => ({
          id: n.id,
          name: n.name ?? n.id,
          labels: n.labels ?? [],
          // Honour a caller-provided `type` (e.g. 'StickyNote' for a note with
          // isSticky=true — Neo4j only has the :Note label, the sticky variant
          // is a UI-side typeLabel for cytoscape styling). Fall back to the
          // first Neo4j label for everything else.
          typeLabel: n.type ?? n.labels?.[0],
          properties: n.properties ?? {},
        })),
        edges: newEdges.map(e => ({
          id: e.id,
          type: e.type,
          source: e.source,
          target: e.target,
          properties: e.properties ?? {},
        })),
        view: emptyViewState(),
      }
      state.addToObjectSet(view, 'Expanded entities', 'expand')
    },
    [storeApi]
  )

  // Find Path outcome handlers — both clear the banner and the highlight.
  const dismissPathResult = useCallback(() => {
    // Always invalidate any in-flight request so a late response can't
    // resurrect the banner (e.g. after Create Connection was armed mid-flight).
    pathRequestRef.current++
    // Guarded: '.path-highlight' is shared with the AI assistant's Highlight
    // action — don't wipe it when there is no path result to dismiss.
    if (!pathResultRef.current) return
    setPathResult(null)
    const cy = graphRef.current?.getCy?.()
    if (cy) clearPathHighlightOnCanvas(cy)
  }, [])

  const keepPathEntities = useCallback(() => {
    if (!pathResult) return
    pathRequestRef.current++
    // Provisional elements were diffed against the *rendered* view; re-check
    // against the full object set so a filter-hidden node isn't added twice.
    const baseIds = new Set(baseNodes.map(n => n.id))
    const baseEdgeIds = new Set(edges.map(e => e.id))
    mergeIntoView(
      pathResult.addedNodes.filter(n => !baseIds.has(n.id)),
      pathResult.addedEdges.filter(e => !baseEdgeIds.has(e.id))
    )
    // Kept entities that an active filter hides vanish the moment the
    // provisional layer clears — say so instead of looking like Keep failed.
    const { hiddenEntityTypes, hiddenEntityIds } = exclusionState.exclusion
    const hiddenTypes = new Set(hiddenEntityTypes)
    const hiddenIds = new Set(hiddenEntityIds)
    const hiddenKept = pathResult.addedNodes.filter(
      n =>
        hiddenTypes.has(n.typeLabel || 'Unknown') ||
        hiddenIds.has(n.id) ||
        hiddenContributionNodeIds.has(n.id)
    ).length
    if (hiddenKept > 0) {
      toast.info(t('investigations.graph.pathTool.result.keptHidden', { count: hiddenKept }))
    }
    setPathResult(null)
    const cy = graphRef.current?.getCy?.()
    if (cy) clearPathHighlightOnCanvas(cy)
  }, [
    pathResult,
    baseNodes,
    edges,
    mergeIntoView,
    exclusionState.exclusion,
    hiddenContributionNodeIds,
    t,
  ])

  // Ids rendered provisionally (on canvas, not in the object set). Ring-menu
  // and selection handlers below special-case them.
  const provisionalNodeIds = useMemo(
    () => new Set(pathResult?.addedNodes.map(n => n.id) ?? []),
    [pathResult]
  )

  // Apply the highlight once the provisional elements are on the canvas —
  // CytoscapeGraph's data-sync effect runs before this parent effect, so the
  // whole path (including just-added nodes) is stylable by the time we run.
  useEffect(() => {
    if (!pathResult) return
    const cy = graphRef.current?.getCy?.()
    if (!cy) return
    applyPathHighlight(cy, pathResult.nodeIds, pathResult.edgeIds)
  }, [pathResult])

  // What actually reaches the canvas: the filtered view plus any provisional
  // Find Path additions awaiting the user's keep/dismiss decision. Deduped
  // and endpoint-validated so the combined arrays can never hand cytoscape a
  // duplicate id or a dangling edge, no matter how the view mutates (filter
  // toggles, expands, chip toggles) while the result banner is open.
  const renderNodes = useMemo<GraphNode[]>(() => {
    if (!pathResult || pathResult.addedNodes.length === 0) return nodes
    const renderedIds = new Set(nodes.map(n => n.id))
    const additions = pathResult.addedNodes.filter(n => !renderedIds.has(n.id))
    return additions.length > 0 ? [...nodes, ...additions] : nodes
  }, [nodes, pathResult])
  const renderEdges = useMemo<GraphEdge[]>(() => {
    if (!pathResult || pathResult.addedEdges.length === 0) return visibleEdges
    const nodeIds = new Set(renderNodes.map(n => n.id))
    const edgeIds = new Set(visibleEdges.map(e => e.id))
    const additions = pathResult.addedEdges.filter(
      e => !edgeIds.has(e.id) && nodeIds.has(e.source) && nodeIds.has(e.target)
    )
    return additions.length > 0 ? [...visibleEdges, ...additions] : visibleEdges
  }, [visibleEdges, renderNodes, pathResult])

  // Create entity — toolbar button or "Create entity here" on the empty
  // canvas → schema-driven dialog → the node joins the investigation's object
  // set (so a saved investigation keeps it) at the right-click point.
  const addNodesToView = useCallback(
    (added: CanvasNode[]) =>
      mergeIntoView(
        added.map(n => ({ ...n, type: n.typeLabel })),
        []
      ),
    [mergeIntoView]
  )
  const entityCreation = useCanvasEntityCreation({
    caseId,
    graphRef,
    nodes: renderNodes,
    addNodes: addNodesToView,
  })

  const onExpandNode = useCallback(
    async (nodeId: string) => {
      if (useSnippetSelection.getState().select(caseId, nodeId)) return
      try {
        const res = await graphApi.getNodeNeighbors(caseId, nodeId)
        const existing = new Set(baseNodes.map(n => n.id))
        const newNodes = res.nodes.filter(n => !existing.has(n.id))
        const existingEdges = new Set(edges.map(e => e.id))
        const newEdges = res.relationships.filter(e => !existingEdges.has(e.id))
        if (newNodes.length === 0 && newEdges.length === 0) {
          toast.info('No new neighbours to add')
          return
        }
        mergeIntoView(newNodes, newEdges)
        toast.success(`Added ${newNodes.length} neighbour${newNodes.length === 1 ? '' : 's'}`)
      } catch (err) {
        toast.error(`Expand failed: ${(err as Error).message}`)
      }
    },
    [baseNodes, edges, caseId, mergeIntoView]
  )

  useEffect(() => {
    if (!pendingLayout) return
    setPendingLayout(false)
    graphRef.current?.runLayout()
  }, [pendingLayout])

  /** Ring-menu entry: look this entity up in the CZ/SK register snapshots.
   *  Only armed for the entity types the registers index, and never for a
   *  compound cluster parent, which is not a real entity. */
  const closeRegisterPicker = useCallback(() => setRegisterAnchorId(null), [])

  const onLookUpInRegisters = useCallback(
    (nodeId: string) => {
      if (isClusterNodeId(nodeId)) return
      if (active?.readOnly) {
        toast.info(t('investigations.detail.description.readOnly'))
        return
      }
      setRegisterAnchorId(nodeId)
    },
    [active?.readOnly, t]
  )

  /** The picked subject's register neighbourhood, written to Neo4j by the API
   *  and handed back as a `GraphView` with real element ids — so it merges into
   *  the object set through the same path as any other addition. The layout is
   *  re-run once the new elements have actually reached the canvas; running it
   *  here would lay out the graph as it was before the import. */
  const onRegistersImported = useCallback(
    (view: GraphView, subjectLabel: string) => {
      const { addedNodeIds } = storeApi
        .getState()
        .addToObjectSet(view, `Register · ${subjectLabel}`, 'expand')
      setPendingLayout(true)
      // Re-importing a subject whose neighbourhood is already on the canvas is
      // a no-op by design (the write MERGEs) — say so rather than "0 added".
      toast.success(
        addedNodeIds.length === 0
          ? t('extensions.osint.picker.importedNone', { name: subjectLabel })
          : t('extensions.osint.picker.imported', {
              count: addedNodeIds.length,
              name: subjectLabel,
            })
      )
    },
    [storeApi, t]
  )

  const startNoteEdit = useCallback((noteId: string, text: string) => {
    setEditingNoteId(noteId)
    setEditingNoteDraft(text)
  }, [])

  const closeNoteEdit = useCallback(() => {
    setEditingNoteId(null)
    setEditingNoteDraft('')
  }, [])

  const saveEditedNote = useCallback(async () => {
    if (!active || !editingNoteId) return
    const next = editingNoteDraft.trim()
    if (!next) return
    setSavingNoteEdit(true)
    try {
      await storeApi.getState().updateActive({
        notes: active.notes.map(note =>
          note.id === editingNoteId ? { ...note, text: next } : note
        ),
      })
      closeNoteEdit()
      toast.success('Note updated')
    } catch (err) {
      toast.error(`Update note failed: ${(err as Error).message}`)
    } finally {
      setSavingNoteEdit(false)
    }
  }, [active, closeNoteEdit, editingNoteDraft, editingNoteId, storeApi])

  const openNoteComposer = useCallback(() => {
    if (!active || active.readOnly) return
    setNotesCollapsed(false)
    setComposingNote(true)
  }, [active])

  const cancelNoteComposer = useCallback(() => {
    setComposingNote(false)
    setNoteDraft('')
  }, [])

  const submitNewNote = useCallback(async () => {
    if (!active) return
    const next = noteDraft.trim()
    if (!next) return
    setSavingNote(true)
    try {
      await storeApi.getState().updateActive({
        notes: [
          {
            id: crypto.randomUUID(),
            text: next,
            authorId: user?.id,
            authorName: user?.username ?? 'Analyst',
            createdAt: new Date().toISOString(),
          },
          ...active.notes,
        ],
      })
      setNoteDraft('')
      setComposingNote(false)
      toast.success('Note added')
    } catch (err) {
      toast.error(`Add note failed: ${(err as Error).message}`)
    } finally {
      setSavingNote(false)
    }
  }, [active, noteDraft, storeApi, user])

  const deleteNote = useCallback(
    async (noteId: string) => {
      if (!active) return
      if (!confirm('Delete this note?')) return
      try {
        await storeApi.getState().updateActive({
          notes: active.notes.filter(note => note.id !== noteId),
        })
        toast.success('Note deleted')
      } catch (err) {
        toast.error(`Delete note failed: ${(err as Error).message}`)
      }
    },
    [active, storeApi]
  )

  // ---------------------------------------------------------------------
  // "+N" expansion-hint badges. We position one HTML chip per node whose
  // `unloadedNeighborCount > 0`, anchored to the node's rendered position.
  // Reads `decoratedBaseNodes` (unfiltered) so chips appear even on nodes
  // currently muted by the type filter — that's deliberate, the user might
  // re-enable a type and want the chip back. Positions update via direct DOM
  // writes on each cytoscape `render` event so panning / zooming stays
  // buttery (no React reconciliation on the hot path).
  const badgeRefs = useRef(new Map<string, HTMLElement>())
  const [expandTarget, setExpandTarget] = useState<{
    node: GraphNode
    // `floor` is the canvas top edge: the phone popover never rises above it.
    anchor: { x: number; y: number; floor: number }
  } | null>(null)

  const expandableNodeIds = useMemo(
    () =>
      decoratedBaseNodes
        .filter(n => (n.unloadedNeighborCount ?? 0) > 0)
        .map(n => n.id)
        .join(','),
    [decoratedBaseNodes]
  )

  const expandableMap = useMemo(() => {
    const m = new Map<string, GraphNode>()
    for (const n of decoratedBaseNodes) {
      if ((n.unloadedNeighborCount ?? 0) > 0) m.set(n.id, n)
    }
    return m
  }, [decoratedBaseNodes])

  // expandableNodeIds is a stable string snapshot of the expandable set —
  // safer than depending on the Map identity, which churns on every memo.
  useNodeCornerAnchors({
    graphRef,
    wrapperRef: canvasWrapperRef,
    chips: badgeRefs,
    idsKey: expandableNodeIds,
    corner: 'top-right',
  })

  // "Changed" markers (PRD-0075 R-C2): a node merged, or with a property changed, in the last
  // 30 days, read by stable id; pinned to the node's top-left corner like the "+N" chips.
  const changedRefs = useRef(new Map<string, HTMLElement>())
  const baseNodeUids = useMemo(
    () => decoratedBaseNodes.flatMap(n => entityUid(n.properties ?? {}) ?? []),
    [decoratedBaseNodes]
  )
  const changedUids = useChangedEntities(caseId, baseNodeUids)
  const changedNodeIds = useMemo(
    () =>
      decoratedBaseNodes
        .filter(n => changedUids.has(entityUid(n.properties ?? {}) ?? ''))
        .map(n => n.id),
    [decoratedBaseNodes, changedUids]
  )
  useNodeCornerAnchors({
    graphRef,
    wrapperRef: canvasWrapperRef,
    chips: changedRefs,
    idsKey: changedNodeIds.join(','),
    corner: 'top-left',
  })

  // ---------------------------------------------------------------------

  if (!view) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Select an investigation from the list to view its graph.
      </div>
    )
  }

  return (
    // `min-h-0` is REQUIRED here, not just on the canvas wrapper below. Without
    // it, this outer flex column can't shrink below its content's intrinsic
    // height — so when the bottom TimeRangeFilter remounts (toggling timeline
    // visibility off → on), the canvas wrapper retains its old larger height
    // and the TimeRangeFilter ends up pushed below the parent's
    // `overflow-hidden` cutoff. With `min-h-0` the column rebalances on every
    // toggle.
    <div className="relative flex min-h-0 flex-1 flex-col bg-background">
      {/* `isolate` makes this a stacking context so the absolutely-positioned
          overlays inside (neighbour badges z-10, notes panel z-20) stay
          contained and never paint over the sibling TimeRangeFilter below. */}
      <div ref={canvasWrapperRef} className="relative isolate min-h-0 flex-1">
        {active && (
          <Card
            className={cn(
              // Phone: stop short of the tool column on the right edge.
              'absolute top-4 left-4 z-20 w-[calc(100%-5.5rem)] max-w-80 rounded-2xl p-0 md:w-80',
              'border border-border/60 bg-accent text-accent-foreground',
              'shadow-[0_8px_24px_-12px_hsl(var(--foreground)/0.18)]'
            )}
          >
            <div className="flex w-full items-center justify-between px-4 py-3">
              <button
                type="button"
                className="flex flex-1 items-center gap-2 text-left max-md:py-2"
                onClick={() => setNotesCollapsed(collapsed => !collapsed)}
              >
                <MessageSquareText className="size-3.5 text-primary" />
                <span className="text-3xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {t('investigations.graph.notesPanel.title')}
                </span>
                <span className="inline-flex h-4 min-w-[20px] items-center justify-center rounded-full border border-border/60 bg-muted px-1.5 text-3xs font-medium tabular-nums text-muted-foreground">
                  {visibleNotes.length}
                </span>
              </button>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground hover:text-foreground md:size-6"
                  onClick={openNoteComposer}
                  disabled={!active || active.readOnly}
                  title={
                    active?.readOnly
                      ? t('investigations.graph.notesPanel.locked')
                      : t('investigations.graph.notesPanel.addNote')
                  }
                >
                  <Plus className="size-4" />
                </Button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground max-md:-m-2 max-md:p-2"
                  onClick={() => setNotesCollapsed(collapsed => !collapsed)}
                  aria-label={
                    notesCollapsed
                      ? t('investigations.graph.notesPanel.expand')
                      : t('investigations.graph.notesPanel.collapse')
                  }
                >
                  {notesCollapsed ? (
                    <ChevronRight className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </button>
              </div>
            </div>

            {!notesCollapsed && (
              <div className="max-h-48 space-y-2 overflow-y-auto border-t border-border/60 px-3 py-3 md:max-h-80">
                {composingNote && (
                  <div className="rounded-lg border border-border bg-card p-2">
                    <Textarea
                      autoFocus
                      value={noteDraft}
                      onChange={e => setNoteDraft(e.target.value)}
                      placeholder={t('investigations.graph.notesPanel.placeholder')}
                      // No font-size override: the primitive's 16px phone text
                      // keeps iOS from zooming the page on focus.
                      className="min-h-20 font-sans bg-card border-border/40"
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 px-2 text-xs md:h-7"
                        onClick={cancelNoteComposer}
                        disabled={savingNote}
                      >
                        {t('investigations.graph.notesPanel.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        className="h-9 px-2 text-xs md:h-7"
                        onClick={submitNewNote}
                        disabled={savingNote || !noteDraft.trim()}
                      >
                        {savingNote
                          ? t('investigations.graph.notesPanel.saving')
                          : t('investigations.graph.notesPanel.save')}
                      </Button>
                    </div>
                  </div>
                )}
                {!composingNote && visibleNotes.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
                    <Trans
                      i18nKey="investigations.graph.notesPanel.empty"
                      components={{ strong: <span className="font-medium text-foreground" /> }}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {visibleNotes.map(note => (
                      <div
                        key={note.id}
                        className="rounded-lg border border-border bg-card p-3 text-left"
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm leading-6 text-foreground">{note.text}</p>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {note.authorName} · {new Date(note.createdAt).toLocaleString()}
                            </p>
                          </div>
                          {!active.readOnly && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-9 shrink-0 text-muted-foreground hover:text-foreground md:size-7"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="size-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-36">
                                <DropdownMenuItem
                                  onSelect={e => {
                                    e.preventDefault()
                                    startNoteEdit(note.id, note.text)
                                  }}
                                >
                                  <Pencil className="mr-2 size-3.5" />
                                  {t('investigations.graph.notesPanel.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => {
                                    void deleteNote(note.id)
                                  }}
                                >
                                  <Trash2 className="mr-2 size-3.5" />
                                  {t('investigations.graph.notesPanel.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        <CytoscapeGraph
          ref={graphRef}
          key={canvasKey}
          caseId={caseId}
          nodes={renderNodes}
          edges={renderEdges}
          dimmedNodeIds={dimmedNodeIds}
          dimmedEdgeIds={dimmedEdgeIds}
          layout={layout}
          onLayoutChange={l => {
            setLayout(l as LayoutId)
            // Persist the new layout choice immediately so it survives reloads.
            // (Node positions are debounced; ui_state is small so flush now.)
            queueMicrotask(() => graphRef.current?.persist())
          }}
          uiState={{ layout }}
          onUiStateLoaded={s => {
            const saved = s?.layout
            if (isValidLayout(saved)) setLayout(saved)
          }}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
          onExpandNode={onExpandNode}
          onCreateEntity={entityCreation.openAt}
          onLookUpInRegisters={osintEnabled ? onLookUpInRegisters : undefined}
          onAddConnection={nodeId => {
            // The two target-picking modes are mutually exclusive, and both
            // banners share the bottom-left corner with the path result.
            // Acting on a provisional path node implies keeping it — commit
            // first so the gesture's source doesn't vanish from the canvas.
            cancelPathPick()
            if (provisionalNodeIds.has(nodeId)) keepPathEntities()
            else dismissPathResult()
            graphConnection.startConnectionMode(nodeId)
          }}
          onFindPathFromHere={nodeId => {
            // Compound cluster parents aren't real entities — don't arm.
            if (isClusterNodeId(nodeId)) return
            graphConnection.cancelConnectionMode()
            if (provisionalNodeIds.has(nodeId)) keepPathEntities()
            else dismissPathResult()
            setPathSourceId(nodeId)
          }}
          onAddNote={nodeId => {
            // Same commit-on-interact rule: a note's HAS_NOTE edge would be
            // dangling if its anchor stayed provisional and got dismissed.
            if (provisionalNodeIds.has(nodeId)) keepPathEntities()
            setAddNoteSourceId(nodeId)
            setAddNoteOpen(true)
          }}
          onToggleFilter={() => setShowFilterPanel(prev => !prev)}
          showFilterPanel={showFilterPanel}
          onNodeSelect={(n: SelectedNode | SelectedEdge | null) => {
            if (n && useSnippetSelection.getState().select(caseId, n.id)) {
              if (!sidebarOpen && onOpenSidebar) onOpenSidebar()
              return
            }
            // Single-click selects and pushes the id through the canonical
            // selection-store so the InvestigationSidebar's matching row
            // auto-expands its inline entity detail (single-row enforced by
            // `replace`).
            //
            // While Create-Connection mode is active, a tap on a *different*
            // node is the target-pick gesture — open the relationship-type
            // dialog instead of selecting.
            //
            // IMPORTANT: do NOT clear `selectedEdgeId` from the n === null
            // branch. Cytoscape fires onNodeSelect(null) when a node is
            // deselected — and that fires AFTER onEdgeSelect(edge) when the
            // user clicks an edge while a node was previously selected.
            // Clearing the edge here would race-stomp the freshly-set edge
            // selection. onEdgeSelect(null) is the right place to clear edges.
            const { replace: replaceSelection, selectEdge } = useSelectionStore.getState()
            if (!n) {
              replaceSelection([], 'graph')
              return
            }
            if ('labels' in n) {
              if (
                graphConnection.isConnectionMode &&
                graphConnection.connectionSourceId &&
                graphConnection.connectionSourceId !== n.id
              ) {
                graphConnection.handleConnectionSelect(
                  n.id,
                  n.name,
                  n.typeLabel,
                  n.labels,
                  id => baseNodes.find(b => b.id === id)?.name || 'Unknown',
                  id => baseNodes.find(b => b.id === id)?.typeLabel,
                  id => baseNodes.find(b => b.id === id)?.labels
                )
                return
              }
              // Same target-pick gesture for the Find Path tool. Compound
              // cluster parents aren't real entities and a re-tap of the
              // source isn't a pick — both fall through to normal selection.
              if (pathSourceId && pathSourceId !== n.id && !isClusterNodeId(n.id)) {
                setPathSourceId(null)
                void findAndHighlightPath(pathSourceId, n.id)
                return
              }
              // Provisional Find Path nodes aren't in the object set yet —
              // the sidebar has no row for them, so don't push them into the
              // cross-view selection (and don't force-open an empty sidebar).
              if (provisionalNodeIds.has(n.id)) return
              // Flag the mirror effect to skip its camera pan — the node
              // is already at the click point; combined with the sidebar
              // slide-in the pan reads as a disruptive jolt.
              skipNextFlyForRef.current = n.id
              replaceSelection([n.id], 'graph')
              selectEdge(null, 'graph')
              // Force-open the sidebar so the user always sees the detail
              // of what they just clicked. If the host doesn't manage the
              // sidebar (no callback) this is a no-op — selection still
              // lands in the store for whatever else is listening.
              if (!sidebarOpen && onOpenSidebar) onOpenSidebar()
            }
          }}
          onEdgeSelect={(edge: SelectedEdge | null) => {
            // Edge clicks expand the matching row in the InvestigationSidebar's
            // Relationships section. Sidebar mirrors this through `selectedEdgeId`.
            const { selectEdge, replace: replaceEntitySelection } = useSelectionStore.getState()
            if (edge) {
              // Same skip-pan rationale as for node clicks.
              skipNextFlyForEdgeRef.current = edge.id
              replaceEntitySelection([], 'graph')
              selectEdge(edge.id, 'graph')
              // Same force-open guarantee as for node clicks.
              if (!sidebarOpen && onOpenSidebar) onOpenSidebar()
            } else {
              selectEdge(null, 'graph')
            }
          }}
          onMultiSelect={ids =>
            // Provisional path nodes aren't in the object set — "Remove from
            // investigation" would silently no-op on them, so keep them out
            // of the multi-select entirely.
            setMultiSelectedNodeIds(ids.filter(id => !provisionalNodeIds.has(id)))
          }
          fitOnLoad
          enableLOD
          enableWebGL
          toolbarExtras={
            <>
              {Object.keys(neighborCounts).length > 0 && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => setShowGlobalExpand(true)}
                        className={cn(
                          'p-2 rounded-lg transition-colors',
                          'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                        )}
                      >
                        <Network className="size-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Expand all visible nodes by one hop</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {timeBounds && (
                <ViewToolsFan
                  timelineVisible={timelineVisible}
                  onToggleTimeline={() => setTimelineVisible(v => !v)}
                />
              )}
              {!sidebarOpen && onOpenSidebar && (
                <SidebarOpenButton onClick={onOpenSidebar} className="mt-1" />
              )}
            </>
          }
        />

        <MultiSelectActionBar
          selectedCount={multiSelectedNodeIds.length}
          onClear={() => setMultiSelectedNodeIds([])}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-xs text-red-400 hover:bg-red-500/20 hover:text-red-200 md:h-7"
            onClick={() => {
              storeApi.getState().removeFromObjectSet(multiSelectedNodeIds, [])
              setMultiSelectedNodeIds([])
            }}
          >
            <Trash2 className="mr-1 size-3" />
            {t('investigations.graph.multiSelect.removeFromInvestigation')}
          </Button>
        </MultiSelectActionBar>

        {/* "+N" expansion-hint badges. Rendered once per expandable-set
          change; the effect above writes `style.left/top` directly via refs
          on every cytoscape render to keep panning lag-free. */}
        {Array.from(expandableMap.values()).map(node => {
          const count = node.unloadedNeighborCount ?? 0
          return (
            <button
              key={node.id}
              ref={el => {
                if (el) badgeRefs.current.set(node.id, el)
                else badgeRefs.current.delete(node.id)
              }}
              type="button"
              onClick={e => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                setExpandTarget({
                  node,
                  anchor: {
                    x: rect.left + rect.width / 2,
                    y: rect.top,
                    floor: canvasWrapperRef.current?.getBoundingClientRect().top ?? 0,
                  },
                })
              }}
              title={`${count} unloaded neighbour${count === 1 ? '' : 's'}`}
              className={cn(
                // z-10 keeps the badge above the cytoscape canvas but below
                // the notes panel (z-20). The wrapper's `isolate` confines
                // this context so badges never overlap the timeline below.
                'absolute z-10 inline-flex items-center justify-center rounded-full',
                'bg-primary text-primary-foreground border border-primary-foreground/30',
                'px-1.5 h-5 min-w-[20px] text-3xs font-bold tabular-nums',
                'shadow-md hover:bg-primary/90 transition-colors pointer-events-auto',
                'will-change-transform',
                // Phone: invisible hit area 8px beyond the badge (scales with it).
                "max-md:after:absolute max-md:after:-inset-2 max-md:after:content-['']"
              )}
              // Initial position set off-screen; the rAF write below moves it
              // to the right place on first cytoscape render.
              style={{
                left: -9999,
                top: -9999,
                transform: 'translate(-50%, -50%)',
                transformOrigin: 'center center',
              }}
            >
              +{count}
            </button>
          )
        })}

        {changedNodeIds.map(id => (
          <ChangedNodeMarker
            key={id}
            ref={el => {
              if (el) changedRefs.current.set(id, el)
              else changedRefs.current.delete(id)
            }}
          />
        ))}

        {/* Per-node expand popover, anchored to the badge click point. */}
        {expandTarget && (
          <div
            // Phone: keep the 280px popover inside the viewport when the badge
            // sits near a screen edge (half its width plus an 8px margin), and
            // below the canvas top: the wrapper spans canvas top -> badge and
            // the popover sits at its bottom (`mt-auto`); when it is taller
            // than that gap the auto margin collapses and it hangs from the
            // canvas top instead of sliding under the top bar.
            className={cn(
              'fixed left-[clamp(148px,var(--expand-x),calc(100vw-148px))] z-30 -translate-x-1/2 md:left-(--expand-x)',
              'top-[calc(var(--expand-floor)+8px)] flex h-[max(0px,calc(var(--expand-y)-var(--expand-floor)-8px))] flex-col',
              // The tall phone wrapper must not swallow taps meant for the canvas.
              'pointer-events-none md:pointer-events-auto',
              'md:top-(--expand-y) md:block md:h-auto md:-translate-y-full'
            )}
            style={
              {
                '--expand-x': `${expandTarget.anchor.x}px`,
                '--expand-y': `${expandTarget.anchor.y}px`,
                '--expand-floor': `${expandTarget.anchor.floor}px`,
              } as CSSProperties
            }
          >
            <div className="pointer-events-auto mt-auto shrink-0">
              <ExpandNeighborsPopover
                caseId={caseId}
                nodeId={expandTarget.node.id}
                nodeName={expandTarget.node.name}
                countsByLabel={expandTarget.node.unloadedNeighborsByLabel ?? {}}
                onMerge={async (nNodes, nEdges) => {
                  await mergeIntoView(nNodes, nEdges)
                }}
                onClose={() => setExpandTarget(null)}
              />
            </div>
          </div>
        )}

        <GlobalExpandConfirm
          open={showGlobalExpand}
          onOpenChange={setShowGlobalExpand}
          caseId={caseId}
          counts={neighborCounts}
          onMerge={async (nNodes, nEdges) => {
            await mergeIntoView(nNodes, nEdges)
          }}
        />

        <AnimatePresence initial={false}>
          {showFilterPanel && (
            <motion.aside
              key="investigation-filter-panel"
              initial={{ opacity: 0, x: 32, scale: 0.94, filter: 'blur(10px)' }}
              animate={{ opacity: 1, x: 0, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, x: 24, scale: 0.96, filter: 'blur(8px)' }}
              transition={{ type: 'spring', stiffness: 380, damping: 32, mass: 0.9 }}
              style={{
                right: 'calc(var(--graph-tools-right, 1rem) + 3.5rem)',
                transformOrigin: 'right top',
              }}
              className={cn(
                'absolute top-4 z-30 flex w-[calc(100%-5.5rem)] max-w-[300px] max-h-[55vh] flex-col overflow-hidden rounded-2xl md:w-[300px]',
                'border border-border bg-accent text-accent-foreground',
                'shadow-[0_24px_72px_-18px_hsl(var(--foreground)/0.40),0_6px_18px_-6px_hsl(var(--foreground)/0.22),inset_0_1px_0_hsl(0_0%_100%/0.08)]'
              )}
            >
              <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Filter className="size-3.5 text-muted-foreground" />
                  <span className="text-sm font-semibold">Filters</span>
                  {hiddenTypesCount > 0 && (
                    <span className="rounded-full bg-background/60 px-1.5 py-0.5 text-3xs font-medium text-muted-foreground">
                      {hiddenTypesCount} hidden
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilterPanel(false)}
                  className="text-muted-foreground transition-colors hover:text-foreground max-md:-m-2 max-md:p-2"
                >
                  <X className="size-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-2 py-2">
                {nodeTypeStats.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No entities to filter.
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {nodeTypeStats.map(({ type, count, isHidden, color }) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() =>
                          isHidden
                            ? exclusionState.showEntityType(type)
                            : exclusionState.hideEntityType(type)
                        }
                        className={cn(
                          'group flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left transition-colors',
                          'hover:bg-background/40',
                          isHidden && 'opacity-50'
                        )}
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span
                            className="size-2.5 shrink-0 rounded-full border"
                            style={{ backgroundColor: color.fill, borderColor: color.stroke }}
                          />
                          <span className="truncate text-sm">{type}</span>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 text-muted-foreground">
                          <span className="text-2xs">{count}</span>
                          {isHidden ? (
                            <EyeOff className="size-3.5" />
                          ) : (
                            <Eye className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <NoLongerValidToggle className="border-t border-border/60 px-4 py-2" />

              {hiddenTypesCount > 0 && (
                <div className="border-t border-border/60 px-3 py-2">
                  <button
                    type="button"
                    onClick={exclusionState.clearHiddenEntityTypes}
                    className="w-full rounded-md px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-background/40 hover:text-foreground"
                  >
                    Show all
                  </button>
                </div>
              )}
            </motion.aside>
          )}
        </AnimatePresence>

        <Dialog open={editingNoteId !== null} onOpenChange={open => !open && closeNoteEdit()}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('investigations.graph.notesPanel.editDialog.title')}</DialogTitle>
              <DialogDescription>
                {t('investigations.graph.notesPanel.editDialog.description')}
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={editingNoteDraft}
              onChange={e => setEditingNoteDraft(e.target.value)}
              placeholder={t('investigations.graph.notesPanel.editDialog.placeholder')}
              className="min-h-28"
            />
            <DialogFooter>
              <Button variant="outline" onClick={closeNoteEdit}>
                {t('investigations.graph.notesPanel.cancel')}
              </Button>
              <Button
                onClick={() => void saveEditedNote()}
                disabled={!editingNoteDraft.trim() || savingNoteEdit}
              >
                {savingNoteEdit
                  ? t('investigations.graph.notesPanel.saving')
                  : t('investigations.graph.notesPanel.editDialog.saveChanges')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sidebarSlot}

        {registerAnchor && (
          // Keyed on the anchor so each open seeds its own search state from the
          // entity it was opened on — the picker holds that state internally.
          <RegisterPicker
            key={registerAnchor.id}
            caseId={caseId}
            anchor={registerAnchor}
            onClose={closeRegisterPicker}
            onImported={onRegistersImported}
          />
        )}

        <CreateNoteDialog
          open={addNoteOpen}
          onOpenChange={open => {
            setAddNoteOpen(open)
            if (!open) setAddNoteSourceId(null)
          }}
          caseId={caseId}
          nodeId={addNoteSourceId}
          onSuccess={result => {
            // The investigation's graphView is a PostgreSQL snapshot — it
            // does not auto-pull live Neo4j writes. Merge the new :Note and
            // its HAS_NOTE edges into the local view (same pattern as
            // createRelationship → mergeIntoView above) so the note appears
            // immediately and the user can Save to persist.
            const isSticky = Boolean(result.properties?.isSticky)
            const newNode: NeighborNode = {
              id: result.element_id,
              name: result.name,
              labels: result.labels,
              type: isSticky ? 'StickyNote' : 'Note',
              properties: result.properties,
            }
            const newEdges: NeighborEdge[] = result.edges.map(e => ({
              id: e.id,
              source: e.source,
              target: e.target,
              type: e.type,
              properties: e.properties,
            }))
            mergeIntoView([newNode], newEdges)
          }}
        />

        <CreateRelationshipDialog
          open={graphConnection.showRelationshipDialog}
          onOpenChange={graphConnection.setShowRelationshipDialog}
          caseId={caseId}
          sourceEntity={graphConnection.pendingConnection?.source || null}
          targetEntity={graphConnection.pendingConnection?.target || null}
          onCreateRelationship={async option => {
            // Divergence pre-check: a curated investigation can drift away
            // from the source case data — an entity may have been merged,
            // deleted, or otherwise removed from Neo4j since this view was
            // loaded. The local view alone is not authoritative; we must
            // probe Neo4j directly.
            const src = graphConnection.pendingConnection?.source
            const tgt = graphConnection.pendingConnection?.target
            if (!src || !tgt) return

            const [srcResult, tgtResult] = await Promise.allSettled([
              graphApi.getNode(caseId, src.id),
              graphApi.getNode(caseId, tgt.id),
            ])
            const hasSource = srcResult.status === 'fulfilled'
            const hasTarget = tgtResult.status === 'fulfilled'
            if (!hasSource || !hasTarget) {
              const missing = !hasSource ? src : tgt
              setDivergenceWarning({
                missingId: missing.id,
                missingName: missing.name,
                side: !hasSource ? 'source' : 'target',
              })
              graphConnection.setShowRelationshipDialog(false)
              graphConnection.cancelConnectionMode()
              return
            }
            await graphConnection.createRelationship(option)
          }}
        />

        <CreateEntityDialog {...entityCreation.dialogProps} />

        <Dialog
          open={!!divergenceWarning}
          onOpenChange={open => !open && setDivergenceWarning(null)}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Investigation diverged from case data</DialogTitle>
              <DialogDescription>
                The {divergenceWarning?.side} entity{' '}
                <span className="font-semibold text-foreground">
                  "{divergenceWarning?.missingName}"
                </span>{' '}
                no longer exists in this case's graph. It may have been merged into another entity,
                deleted, or otherwise removed since this view was loaded.
                <br />
                <br />
                The connection was <span className="font-semibold">not</span> created. Refresh the
                view to sync with the current case data, then try again — possibly against the
                entity it was merged into.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => setDivergenceWarning(null)}>OK</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bottom-left target-pick banner, shared by Create Connection and
            Find Path — at most one mode is armed at a time (the ring-menu
            handlers cancel the other). */}
        <ConnectionTargetBanner
          visible={
            (graphConnection.isConnectionMode && !graphConnection.showRelationshipDialog) ||
            pathSourceId !== null
          }
          sourceName={pickSource ? getEntityDisplayLabel(pickSource) : 'Source entity'}
          sourceTypeLabel={pickSource?.typeLabel}
          onCancel={pathSourceId !== null ? cancelPathPick : graphConnection.cancelConnectionMode}
          instruction={pathSourceId !== null ? PATH_TOOL_INSTRUCTION : undefined}
        />

        {/* Find Path outcome — occupies the same corner as the target-pick
            banner above; arming either pick mode dismisses it first. */}
        <PathResultBanner
          visible={pathResult !== null}
          pathLength={pathResult?.pathLength ?? 0}
          addedCount={pathResult?.addedNodes.length ?? 0}
          onKeep={keepPathEntities}
          onDismiss={dismissPathResult}
        />
      </div>

      {timeBounds && timelineVisible && (
        // `contents` keeps the strip a direct flex child of the column. On a
        // phone the open sidebar overlays the whole canvas, so the strip
        // steps aside and hands its height to the sidebar.
        <div className={cn(sidebarSlot && sidebarOpen ? 'hidden md:contents' : 'contents')}>
          <TimeRangeFilter
            view="graph"
            bounds={timeBounds}
            value={graphTimeFilter}
            onChange={range => setGraphTimeFilter('graph', range)}
            density={timeDensity}
            timestamps={timeDensityStamps}
            total={visibleNodes.length}
            visibleCount={timeFilteredVisibleCount}
            onAutoFit={() => setGraphTimeFilter('graph', null)}
            countSuffix="entities"
          />
        </div>
      )}
    </div>
  )
}

function firstSelectedId(set: ReadonlySet<string>): string | null {
  if (set.size === 0) return null
  return set.values().next().value ?? null
}

// Hoisted: fully static element tree, and the banner only shows it while a
// Find Path pick gesture is in flight.
const PATH_TOOL_INSTRUCTION = (
  <Trans
    i18nKey="investigations.graph.pathTool.instruction"
    components={{ strong: <span className="text-foreground/90 font-medium" /> }}
  />
)

/**
 * Animate the camera to centre on `node`, keeping the current zoom. Uses an
 * RAF interpolation against `cy.pan()` because `cy.animate({ center })` /
 * `cy.animate({ pan })` are no-ops in this configuration — sync pan works
 * fine, animated pan does not. ~400ms ease-in-out-quad mirrors the map view.
 */
function flyToNode(
  cy: {
    pan: (p?: { x: number; y: number }) => { x: number; y: number }
    zoom: () => number
    width: () => number
    height: () => number
  },
  node: { position: () => { x: number; y: number } }
) {
  flyToPoint(cy, node.position())
}

function flyToPoint(
  cy: {
    pan: (p?: { x: number; y: number }) => { x: number; y: number }
    zoom: () => number
    width: () => number
    height: () => number
  },
  point: { x: number; y: number }
) {
  const z = cy.zoom()
  const startPan = { ...cy.pan() }
  const targetPan = { x: cy.width() / 2 - point.x * z, y: cy.height() / 2 - point.y * z }
  const start = performance.now()
  const duration = 400
  const step = (now: number) => {
    const t = Math.min((now - start) / duration, 1)
    const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
    cy.pan({
      x: startPan.x + (targetPan.x - startPan.x) * eased,
      y: startPan.y + (targetPan.y - startPan.y) * eased,
    })
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

// View-tools fan-out button. Mirrors the map's `ToolsFanButton` (see
// map-toolbar.tsx): a trigger that springs a satellite out to the LEFT
// when opened. The graph only needs one action — toggle the bottom
// time-range strip visibility — so the fan collapses to a single Clock
// satellite.
//
// Implementation note: this used to drive the satellite via framer-motion
// `animate` prop, but InvestigationCanvas re-renders on every cytoscape
// event (selection, layout tick, hover). Passing a fresh animate-target
// object each render intermittently stranded the opacity animation at its
// starting value while transform still progressed — the satellite stayed
// invisible after re-opening the fan, which is the bug the user reported.
// CSS transitions sidestep that: they're declarative, can't be stranded
// mid-batch, and are indifferent to how often the parent re-renders.
function ViewToolsFan({
  timelineVisible,
  onToggleTimeline,
}: {
  timelineVisible: boolean
  onToggleTimeline: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title="View tools"
        className={cn(
          'p-2 rounded-lg transition-colors flex-shrink-0',
          open || timelineVisible
            ? 'bg-primary text-primary-foreground'
            : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
        )}
      >
        <Layers2 className="size-5" />
      </button>
      <div
        className={cn(
          'absolute left-0 top-0 transition-[opacity,transform] duration-200 ease-out',
          open
            ? 'opacity-100 -translate-x-[52px] scale-100 pointer-events-auto'
            : 'opacity-0 translate-x-0 scale-50 pointer-events-none'
        )}
        aria-hidden={!open}
      >
        <button
          type="button"
          tabIndex={open ? 0 : -1}
          onClick={() => {
            onToggleTimeline()
            setOpen(false)
          }}
          title={timelineVisible ? 'Hide timeline' : 'Show timeline'}
          className={cn(
            'p-2 rounded-lg transition-colors',
            timelineVisible
              ? 'bg-primary text-primary-foreground'
              : 'bg-background border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
          )}
        >
          <Clock className="size-5" />
        </button>
      </div>
    </div>
  )
}
